<template>
  <ApplicationData :id="id">
    <div class="application-dialog" slot-scope="{ application, loading }">
      <can I="read" a="OpportunityApplication">
        <!-- Top Floating Header -->
        <v-fade-transition name="v-fade-transition">
          <v-card
            v-show="showFloatingHeader"
            class="text-xs-center floating-profile-header"
            flat
            tile
          >
            <v-layout row align-start justify-space-around class="mt-2 mx-1">
              <v-flex xs4 class="text-xs-left">
                <v-layout v-if="!isWithdrawn" row :wrap="$vuetify.breakpoint.smAndDown">
                  <v-menu
                    :close-on-content-click="false"
                    max-height="50vh"
                    max-width="400"
                    offset-y
                    bottom
                    right
                  >
                    <v-btn slot="activator" class="v-btn--xs" color="secondary">
                      <v-icon small>mdi-folder</v-icon>
                    </v-btn>
                    <v-card dark>
                      <ApplicationDocuments v-if="!loading" :application="application" />
                    </v-card>
                  </v-menu>

                  <v-menu
                    v-if="application.questionnaires && application.questionnaires[0]"
                    :close-on-content-click="false"
                    max-height="50vh"
                    max-width="400"
                    offset-y
                    bottom
                  >
                    <v-btn slot="activator" class="v-btn--xs" color="secondary">
                      <v-icon small>mdi-forum</v-icon>
                    </v-btn>
                    <v-card dark>
                      <ApplicationQuestionnaire :value="application.questionnaires" />
                    </v-card>
                  </v-menu>

                  <v-menu
                    v-if="application.psychometrics && application.psychometrics.filter(p => p.show || p.provider != 'Odyssey' || p.aggregate >= 2).length"
                    max-height="50vh"
                    max-width="400"
                    offset-y
                    bottom
                  >
                    <v-btn slot="activator" class="v-btn--xs" color="secondary">
                      <v-icon small>mdi-account-search</v-icon>
                    </v-btn>
                    <ApplicationPsychometrics :application="application" />
                  </v-menu>

                  <ApplicationsExportSelector v-if="application.opportunity" :value="[application.id]">
                    <v-btn slot="activator" color="secondary" class="v-btn--xs">
                      <v-icon small>mdi-file-pdf-box</v-icon>
                    </v-btn>
                  </ApplicationsExportSelector>
                </v-layout>
              </v-flex>

              <v-flex xs4>
                <div class="text-xs-center" style="margin-top: -40px;">
                  <v-avatar size="80" v-if="application.profile && application.profile.avatar">
                    <img :src="application.profile.avatar" />
                  </v-avatar>
                </div>
                <div class="headline" v-if="application.profile">
                  {{ `${application.profile.firstName} ${application.profile.lastName}` }}
                </div>
                <div class="rating-wrapper">
                  <ApplicationRating :application="application" />
                </div>
              </v-flex>
              <v-flex xs4>
                <v-layout row>
                  <v-flex class="text-xs-right pa-0" v-if="application.profile">
                    <v-tooltip bottom>
                      <v-btn
                        slot="activator"
                        v-clipboard:copy="application.profile.email"
                        @click="copiedToast('email')"
                        color="secondary"
                        small
                        icon
                      >
                        <v-icon small>mdi-email</v-icon>
                      </v-btn>
                      <span>
                        {{ application.profile.email }}<br />
                        Click to copy
                      </span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <v-btn
                        slot="activator"
                        v-clipboard:copy="application.profile.mobileNumber"
                        @click="copiedToast('mobile')"
                        color="secondary"
                        small
                        icon
                      >
                        <v-icon small>mdi-phone</v-icon>
                      </v-btn>
                      <span>
                        +{{ application.profile.mobileNumber }}<br />
                        Click to copy
                      </span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <v-menu offset-y left slot="activator">
                        <v-btn small icon slot="activator" color="secondary">
                          <v-icon small>mdi-information-variant</v-icon>
                        </v-btn>
                        <v-card>
                          <PersonalDetails
                            :profile="application.profile"
                            :actionItems="application.actionItems"
                          />
                        </v-card>
                      </v-menu>
                      <span>View Personal Information</span>
                    </v-tooltip>

                    <!-- Eligibility Details -->
                    <v-tooltip
                      v-if="application.eligibility && application.eligibility.eligible === false"
                      bottom
                    >
                      <v-menu offset-y left slot="activator">
                        <v-btn slot="activator" icon small color="error">
                          <v-icon small>mdi-account-alert</v-icon>
                        </v-btn>
                        <v-card>
                          <v-subheader :value="true">
                            Application no longer eligible
                          </v-subheader>
                        </v-card>
                      </v-menu>
                      <span> Application Ineligible </span>
                    </v-tooltip>

                    <!-- Completion Details -->
                    <v-tooltip bottom v-else>
                      <v-menu offset-y left slot="activator">
                        <v-btn
                          slot="activator"
                          v-if="application.actionItems && application.actionItems[0]"
                          color="warning"
                          small
                          icon
                        >
                          <v-icon small>mdi-account-question</v-icon>
                        </v-btn>
                        <v-btn
                          slot="activator"
                          v-else
                          color="success"
                          small
                          icon
                        >
                          <v-icon small>mdi-account-check</v-icon>
                        </v-btn>
                        <v-card>
                          <ApplicationCompletionDetails :application="application" />
                        </v-card>
                      </v-menu>
                        <span v-if="application.actionItems && application.actionItems[0]">
                          Application Incomplete
                        </span>
                        <span v-else> Application Complete </span>
                    </v-tooltip>
                  </v-flex>
                </v-layout>
              </v-flex>
            </v-layout>
          </v-card>
        </v-fade-transition>

        <!-- Application Dialog -->
        <v-dialog
          @update:returnValue="$emit('close')"
          transition="scale-transition"
          :value="id > 0"
          width="70vw"
          lazy
        >
          <!-- Loading Indicator -->
          <v-card :class="{ 'pt-3': showFloatingHeader }">
            <div v-if="loading" key="application-loading-indicator">
              <div class="text-xs-center pa-3">
                <v-progress-circular size="150" indeterminate>
                  <v-avatar size="140" v-if="application.profile && application.profile.avatar">
                    <img :src="application.profile.avatar" />
                  </v-avatar>
                </v-progress-circular>
                <div
                  v-if="application && application.profile"
                  v-html="`${application.profile.firstName} ${application.profile.lastName}`"
                  class="headline"
                />
              </div>
            </div>
            <!-- Scrollable Application Div -->
            <div
              v-else
              :key="`application-details-${id}`"
              id="profile-container"
              v-scroll:#profile-container="onScroll"
              style="overflow-y: scroll; overflow-x: hidden; margin-bottom: 3rem;"
              :style="{ 'max-height': profileHeight }"
            >
              <Application :application="application" />
              <Eligibility :value="application.eligibility" />
            </div>
            <!-- Bottom Toolbar -->
            <v-card-title class="pa-0" v-if="!loading" color="transparent">
              <can I="edit" a="OpportunityApplication">
                <CandidateQuickActionToolbar
                  :candidate="application"
                  @done="$emit('close')"
                  color="transparent"
                />
              </can>
            </v-card-title>
          </v-card>
        </v-dialog>
      </can>
    </div>
  </ApplicationData>
</template>

<script>
// Components
import Application from '@/components/applications_tracker/applications/Application'
import ApplicationCompletionDetails from '@/components/applications_tracker/applications/ApplicationCompletionDetails'
import ApplicationData from '@/components/applications_tracker/applications/ApplicationData'
import ApplicationDocuments from '@/components/applications_tracker/applications/ApplicationDocuments'
import ApplicationPsychometrics from '@/components/applications_tracker/applications/ApplicationPsychometrics'
import ApplicationQuestionnaire from '@/components/applications_tracker/applications/ApplicationQuestionnaire'
import ApplicationRating from '@/components/applications_tracker/applications/ApplicationRating'
import ApplicationsExportSelector from '@/components/applications_tracker/applications/ApplicationsExportSelector'
import CandidateQuickActionToolbar from '@/components/applications_tracker/CandidateQuickActionToolbar'
import Eligibility from '@/components/applications/Eligibility'
import PersonalDetails from '@/components/applications_tracker/profile/PersonalDetails'

// helpers
import { mapGetters } from 'vuex'
import _ from 'lodash'

export default {
  props: { id: Number },
  components: {
    Application,
    ApplicationCompletionDetails,
    ApplicationData,
    ApplicationDocuments,
    ApplicationPsychometrics,
    ApplicationQuestionnaire,
    ApplicationRating,
    ApplicationsExportSelector,
    CandidateQuickActionToolbar,
    Eligibility,
    PersonalDetails,
  },
  data() {
    return {
      showFloatingHeader: false,
      width: 750,
      canEdit: true,
    }
  },

  computed: {
    isWithdrawn() {
      return this.status === 'Withdrawn'
    },

    profileHeight() {
      switch (this.$vuetify.breakpoint.name) {
        case 'xs':
          return '80vh'
        case 'sm':
          return '82vh'
        case 'md':
          return '82vh'
        case 'lg':
          return '82vh'
        case 'xl':
          return '82vh'
      }
    },
  },
  methods: {
    onScroll(e) {
      this.showFloatingHeader = e.target.scrollTop > 150
    },

    copiedToast(value) {
      this.$store.dispatch('snackbarShow', `Copied ${value}.`)
    },
  },
}
</script>

<style>
div.application-dialog div.rating-wrapper fieldset {
  position: relative;
}

.application-dialog nav div.v-toolbar__content {
  padding: 0;
}

.application-dialog .floating-profile-header {
  position: fixed;
  top: 6vh;
  width: 70vw;
  z-index: 210;
  left: 50%;
  transform: translateX(-50%);
}

.application-dialog .floating-profile-header .v-btn--xs,
.application-dialog .floating-profile-header .v-btn--xs::before {
  min-width: 0;
  width: 44px;
  height: 28px;
}
</style>
